import { z } from 'zod';
import { moneySchema } from '../money';
import { cutLengthMmSchema } from '../inventory/cut-variant';
import { incotermSchema } from '../documents/incoterms';
import { expectedUpdatedAtSchema } from '../documents/document-concurrency';

/** Bestellung (Modul procurement, ADR 0009). `POOL` = Bestellpool (ADR 0041). */
export const purchaseOrderStatusSchema = z.enum(['POOL', 'DRAFT', 'ORDERED', 'RECEIVED', 'CANCELLED']);
export type PurchaseOrderStatus = z.infer<typeof purchaseOrderStatusSchema>;

/**
 * Belegart im Einkauf (Belegfluss Anfrage → Bestellung → Eingangsrechnung).
 * Gegenstück zum Verkaufs-`SalesDocumentKind`. Bestand: alle Alt-Belege = BESTELLUNG.
 */
export const purchaseDocumentKindSchema = z.enum(['ANFRAGE', 'BESTELLUNG', 'EINGANGSRECHNUNG']);
export type PurchaseDocumentKind = z.infer<typeof purchaseDocumentKindSchema>;

/**
 * Erlaubte Umwandlungen (Quelle → mögliche Ziele), gespiegelt im Backend-Service
 * (`PurchaseOrderService.convert`). Analog zu `ALLOWED_CONVERSIONS` im Verkauf.
 */
export const PURCHASE_ALLOWED_CONVERSIONS: Record<PurchaseDocumentKind, PurchaseDocumentKind[]> = {
  ANFRAGE: ['BESTELLUNG'],
  BESTELLUNG: ['EINGANGSRECHNUNG'],
  EINGANGSRECHNUNG: [],
};

/**
 * Manuell setzbare Status je Belegart (die 4 Basis-Status werden geteilt, die
 * Labels je Belegart liegen im Frontend `procurementMeta`). „RECEIVED" (Geliefert)
 * ergibt nur bei der Bestellung Sinn und wird ohnehin über den Wareneingang gesetzt.
 */
export const PURCHASE_ALLOWED_STATUSES_BY_KIND: Record<PurchaseDocumentKind, PurchaseOrderStatus[]> = {
  ANFRAGE: ['DRAFT', 'ORDERED', 'CANCELLED'],
  // POOL = Bestellpool (ADR 0041): gültiger Status einer Bestellung, damit Pool-Belege
  // im Editor sauber round-trippen. Freigabe/Zusammenfassen laufen über eigene Aktionen.
  BESTELLUNG: ['POOL', 'DRAFT', 'ORDERED', 'RECEIVED', 'CANCELLED'],
  EINGANGSRECHNUNG: ['DRAFT', 'ORDERED', 'CANCELLED'],
};

/**
 * Deutsche Status-Bezeichnungen für Server-Meldungen (Befund 2026-09-28: dort
 * stand der rohe Code, „im Status RECEIVED"). Die Oberfläche hat dieselben Worte
 * in `procurementMeta` (dort zusätzlich je Belegart, z. B. „Angefragt").
 */
export const PURCHASE_STATUS_LABELS: Record<PurchaseOrderStatus, string> = {
  POOL: 'Im Pool',
  DRAFT: 'Entwurf',
  ORDERED: 'Bestellt',
  RECEIVED: 'Geliefert',
  CANCELLED: 'Storniert',
};

/**
 * Status, die nur das System setzt — nie von Hand (Befund 2026-09-28): „RECEIVED"
 * (Geliefert) setzt der Wareneingang, sobald alle Positionen eingegangen sind.
 * Von Hand gesetzt stand eine Bestellung ohne jeden Eingang auf „Geliefert",
 * verschwand aus den erwarteten Lieferungen und sperrte den Wareneingang.
 * Ein Teil-Eingang ist kein eigener Status — die Bestellung bleibt „Bestellt".
 */
export const PURCHASE_SYSTEM_STATUSES: PurchaseOrderStatus[] = ['RECEIVED'];

export const createPurchaseOrderSchema = z.object({
  kind: purchaseDocumentKindSchema.default('BESTELLUNG'),
  supplierId: z.string().uuid(),
  projectId: z.string().uuid().optional(),
  language: z.enum(['DE', 'EN']).optional(),
  currency: z.enum(['EUR', 'USD', 'CHF']).default('EUR'),
  /** Ohne Angabe: Inlandssatz der bestellenden Gesellschaft (AT 20 / DE 19). */
  taxRate: z.number().min(0).max(100).optional(),
  orderDate: z.string().datetime().optional(),
  expectedDate: z.string().datetime().optional(),
  dueDate: z.string().datetime().optional(),
  notes: z.string().trim().max(5000).optional(),
  /**
   * Absender: bestellende Gesellschaft. Ohne Angabe leitet der Server sie ab
   * (Projekt-Eigentümer → Lieferanten-Eigentümer → Land). Bestimmt den
   * Nummernkreis und damit die Belegnummer (ADR 0059).
   */
  legalEntityId: z.string().uuid().nullable().optional(),
});
export type CreatePurchaseOrderInput = z.infer<typeof createPurchaseOrderSchema>;

/** „Beleg wandeln" im Einkauf — erzeugt einen Folgebeleg (siehe PURCHASE_ALLOWED_CONVERSIONS). */
export const convertPurchaseOrderSchema = z.object({
  targetKind: purchaseDocumentKindSchema,
});
export type ConvertPurchaseOrderInput = z.infer<typeof convertPurchaseOrderSchema>;

/** Bestellpool (ADR 0041): mehrere Pool-Bestellungen desselben Lieferanten zusammenfassen. */
export const mergePoolOrdersSchema = z.object({
  orderIds: z.array(z.string().uuid()).min(2),
});
export type MergePoolOrdersInput = z.infer<typeof mergePoolOrdersSchema>;

export const updatePurchaseOrderSchema = z.object({
  status: purchaseOrderStatusSchema.optional(),
  language: z.enum(['DE', 'EN']).optional(),
  taxRate: z.number().min(0).max(100).optional(),
  orderDate: z.string().datetime().optional(),
  expectedDate: z.string().datetime().nullable().optional(),
  dueDate: z.string().datetime().nullable().optional(),
  notes: z.string().trim().max(5000).nullable().optional(),
  projectId: z.string().uuid().nullable().optional(),
  /** Lieferbedingung (Incoterms 2020) + Erfüllungsort — Spiegel des Verkaufs. */
  incoterm: incotermSchema.nullable().optional(),
  incotermLocation: z.string().trim().max(200).nullable().optional(),
  /** Sachbearbeiter: Druck-Text („Unser Zeichen") + User-Verknüpfung (Soft-Ref). */
  internalReference: z.string().trim().max(120).nullable().optional(),
  assigneeUserId: z.string().uuid().nullable().optional(),
  /**
   * Lieferant wechseln — nur im Entwurf (nicht im Pool: der Pool bündelt je
   * Lieferant). Name/Anschrift/Sprache werden neu aus dem CRM übernommen, der
   * Ansprechpartner wird geleert (außer eine passende contactId kommt mit).
   */
  supplierId: z.string().uuid().optional(),
  /** Ansprechpartner beim Lieferanten („z. Hd."); muss zum Lieferanten gehören, null entfernt ihn. */
  contactId: z.string().uuid().nullable().optional(),
  /** Kopftext (Markdown) — vor der Positionstabelle gedruckt; leer = keiner. */
  headerText: z.string().trim().max(5000).nullable().optional(),
  /** Fußtext (Markdown) — nach dem Summenblock gedruckt; leer = keiner. */
  footerText: z.string().trim().max(5000).nullable().optional(),
  /** Belegwährung — nur im Entwurf/Pool änderbar; reine Umbenennung ohne Umrechnung. */
  currency: z.enum(['EUR', 'USD', 'CHF']).optional(),
  /** Feld-Keys, die NICHT aufs PDF gedruckt werden (siehe PURCHASE_PDF_FIELDS). */
  pdfHiddenFields: z.array(z.string().trim().max(60)).max(50).optional(),
  /**
   * Absender wechseln. Im Entwurf nummeriert der Server den Beleg um (die
   * Nummer stammt aus dem Nummernkreis der Gesellschaft); ist das nicht mehr
   * zulässig, antwortet er mit 409 und dem Verweis auf den Storno-Weg.
   */
  legalEntityId: z.string().uuid().nullable().optional(),
  /** Optimistische Sperre (ADR 0115): Stand, auf dem die Eingabe beruht; abweichend ⇒ 409. */
  expectedUpdatedAt: expectedUpdatedAtSchema,
});
export type UpdatePurchaseOrderInput = z.infer<typeof updatePurchaseOrderSchema>;

/**
 * Optionale Bestell-Felder, deren PDF-Druck pro Beleg abschaltbar ist. Keys =
 * Vertrag zwischen Editor-UI und PDF-Renderer; `pdfHiddenFields` speichert die
 * abgewählten Keys. Pflichtinhalte (Lieferant, Bestelldatum, Positionen) bleiben.
 */
export const PURCHASE_PDF_FIELDS = [
  { key: 'contact', label: 'Ansprechpartner (z. Hd.)' },
  { key: 'expectedDate', label: 'Liefertermin' },
  { key: 'internalReference', label: 'Sachbearbeiter / Unser Zeichen' },
  { key: 'projectCode', label: 'Projekt' },
  { key: 'incoterm', label: 'Incoterm / Erfüllungsort' },
  { key: 'notes', label: 'Notiz' },
  /** Positions-Langtext: druckt zusätzlich Projektnummer + -name der Position (falls gesetzt). */
  { key: 'itemProjectReference', label: 'Projekt-Bezug im Positions-Langtext' },
  { key: 'headerText', label: 'Kopftext' },
  { key: 'footerText', label: 'Fußtext' },
] as const;
export type PurchasePdfFieldKey = (typeof PURCHASE_PDF_FIELDS)[number]['key'];

export const purchaseItemInputSchema = z.object({
  articleId: z.string().uuid().optional(),
  /**
   * Frei erfasste/importierte Artikelnummer OHNE Katalog-Verknüpfung.
   * Wird nur verwendet, wenn keine `articleId` gesetzt ist (sonst gewinnt der Katalog-Snapshot).
   */
  articleNumber: z.string().trim().max(60).nullable().optional(),
  /** Positionstyp: Artikel (mit Menge/Preis), Text, Überschrift oder Seitenumbruch. */
  positionType: z.enum(['ARTICLE', 'TEXT', 'HEADING', 'PAGE_BREAK']).optional(),
  description: z.string().trim().min(1).max(500),
  longText: z.string().trim().max(5000).optional(),
  unit: z.string().trim().max(20).optional(),
  // 0 erlaubt für Text-/Überschriftszeilen (kein Mengen-/Preisbezug).
  quantity: z.number().nonnegative(),
  unitPrice: moneySchema,
  /** Positions-Projekt (Soft-Ref). Default = Beleg-Kopf-Projekt, je Zeile überschreibbar. */
  projectId: z.string().uuid().nullable().optional(),
  /** Ablängung: geschnittene Länge in mm (Positions-Parameter, ADR 0040). */
  cutLengthMm: cutLengthMmSchema.nullable().optional(),
  /** Gedruckter Nummer-Anhang unter der Artikelnummer (z. B. „L = 2300 mm"). */
  articleNumberSuffix: z.string().trim().max(40).nullable().optional(),
  /** Sortier-Position (0-basiert). Steuert die Reihenfolge der Positionen (Drag & Drop). */
  position: z.number().int().nonnegative().optional(),
});
export type PurchaseItemInput = z.infer<typeof purchaseItemInputSchema>;
export const updatePurchaseItemSchema = purchaseItemInputSchema.partial();
export type UpdatePurchaseItemInput = z.infer<typeof updatePurchaseItemSchema>;

/**
 * Eine Zeile beim **Ersetzen aller Positionen** (`PUT …/items`) — Gegenstück zu
 * `replaceSalesItemSchema` im Verkauf. `id` gesetzt ⇒ bestehende Position, `id`
 * fehlt ⇒ neue. Kein `position`: die Array-Reihenfolge IST die Reihenfolge.
 */
export const replacePurchaseItemSchema = purchaseItemInputSchema
  .omit({ position: true })
  .extend({ id: z.string().uuid().optional() });
export type ReplacePurchaseItemInput = z.infer<typeof replacePurchaseItemSchema>;

/** Vollständige Positionsliste einer Bestellung — was fehlt, wird gelöscht. */
export const replacePurchaseItemsSchema = z.object({
  items: z.array(replacePurchaseItemSchema).max(500),
  /** Optimistische Sperre (ADR 0115): Stand, auf dem die Eingabe beruht; abweichend ⇒ 409. */
  expectedUpdatedAt: expectedUpdatedAtSchema,
});
export type ReplacePurchaseItemsInput = z.infer<typeof replacePurchaseItemsSchema>;

export const purchaseItemResponseSchema = z.object({
  id: z.string().uuid(),
  orderId: z.string().uuid(),
  position: z.number().int(),
  positionType: z.enum(['ARTICLE', 'TEXT', 'HEADING', 'PAGE_BREAK']),
  articleId: z.string().uuid().nullable(),
  articleNumber: z.string().nullable(),
  description: z.string(),
  longText: z.string().nullable(),
  unit: z.string().nullable(),
  quantity: z.number(),
  /** Bereits per Wareneingang abgehakte Menge (Teil-Eingang möglich). */
  receivedQty: z.number(),
  /** Verknüpfte Projekt-Materialposition (Beschaffungs-Spine) oder null. */
  materialItemId: z.string().uuid().nullable(),
  /** Positions-Projekt (Soft-Ref) oder null. */
  projectId: z.string().uuid().nullable(),
  /** Snapshot der Projektnummer für Anzeige/PDF. */
  projectCode: z.string().nullable(),
  /** Snapshot des Projektnamens für Anzeige/PDF (Langtext-Zusatz). */
  projectName: z.string().nullable(),
  unitPrice: moneySchema,
  /** Ablängung: geschnittene Länge in mm (null = keine, ADR 0040). */
  cutLengthMm: z.number().int().nullable(),
  /** Gedruckter Nummer-Anhang unter der Artikelnummer (null = keiner). */
  articleNumberSuffix: z.string().nullable(),
  lineNet: moneySchema,
});
export type PurchaseItemResponse = z.infer<typeof purchaseItemResponseSchema>;

/**
 * Bestellung aus dem offenen Projektbedarf erzeugen (Schritt 7): erzeugt eine
 * DRAFT-Bestellung mit Positionen aus den gewählten Materialpositionen. Menge =
 * offener Bedarf, EK-Preis aus dem Artikelstamm.
 */
export const createOrderFromProjectSchema = z.object({
  supplierId: z.string().uuid(),
  projectId: z.string().uuid(),
  materialItemIds: z.array(z.string().uuid()).min(1).max(500),
  expectedDate: z.string().datetime().optional(),
});
export type CreateOrderFromProjectInput = z.infer<typeof createOrderFromProjectSchema>;

/**
 * Artikel direkt in den Bestellpool legen (Lagercockpit, 2026-09-07): je
 * Standardlieferant entsteht EINE Pool-Bestellung (Status POOL, ADR 0041) mit
 * den gewählten Artikeln und Mengen; EK-Preis aus dem Artikelstamm. Anders als
 * `createOrderFromProjectSchema` ohne Projekt- und Bedarfsbezug — der Bedarf
 * kommt aus dem Lagerbestand (Meldebestand/Reichweite), nicht aus einem Projekt.
 * Artikel ohne Standardlieferant werden abgelehnt (400 mit Artikelnummern).
 *
 * Aus „Auf Verfügbarkeit prüfen" im Verkaufsbeleg (2026-10-15) kommen zwei
 * optionale Angaben dazu: `projectId` (Projekt des Belegs → Positionen und
 * Bestellkopf tragen es) und `sourceReference` (Belegnummer, z. B.
 * „AN-2610-0142" — landet nur im Audit-Log, nicht auf dem Beleg).
 */
export const addArticlesToPoolSchema = z.object({
  items: z
    .array(
      z.object({
        articleId: z.string().uuid(),
        quantity: z.number().positive(),
      }),
    )
    .min(1)
    .max(200),
  projectId: z.string().uuid().optional(),
  sourceReference: z.string().trim().min(1).max(100).optional(),
});
export type AddArticlesToPoolInput = z.infer<typeof addArticlesToPoolSchema>;

export const poolOrderCreatedSchema = z.object({
  orderId: z.string(),
  orderNumber: z.string(),
  supplierId: z.string(),
  supplierName: z.string(),
  itemCount: z.number().int(),
});
export type PoolOrderCreated = z.infer<typeof poolOrderCreatedSchema>;

export const addArticlesToPoolResultSchema = z.object({
  orders: z.array(poolOrderCreatedSchema),
});
export type AddArticlesToPoolResult = z.infer<typeof addArticlesToPoolResultSchema>;

/**
 * Wareneingang abhaken (Schritt 8): optional je Position eine Teilmenge. Ohne
 * `items` wird die jeweils noch offene Restmenge aller Positionen eingebucht.
 */
export const receivePurchaseOrderSchema = z.object({
  items: z
    .array(
      z.object({
        itemId: z.string().uuid(),
        quantity: z.number().positive(),
        /**
         * Mehr einbuchen als bestellt (ADR 0072) — GENAU für diese Position.
         * Standard AUS: ohne dieses Kennzeichen wird auf die offene Restmenge
         * gedeckelt. Nur die Wareneingangsprüfung setzt es, und dort nur mit
         * Pflicht-Bemerkung am Beleg. Bewusst je Zeile statt je Aufruf: ein
         * globales Kennzeichen hob den Mengendeckel auch für Positionen auf,
         * die gar nicht überliefert waren — eine parallel schon abgehakte
         * Position wurde dadurch doppelt gebucht (Audit 2026-08-05).
         */
        allowOverDelivery: z.boolean().optional(),
        /**
         * Eingegangene Menge der Position, die der Client beim Abhaken vor sich
         * hatte (Doppelbuchungsschutz, Befund 2026-09-28). Gesetzt bucht der
         * Server nur, solange die Position noch genau so steht — ein zweiter,
         * identischer Klick läuft ins 409, statt eine Teilmenge doppelt
         * einzubuchen. Ohne Angabe wie bisher (Wareneingangsprüfung: dort
         * schützt der Statuswechsel des Belegs).
         */
        expectedReceivedQty: z.number().min(0).optional(),
      }),
    )
    .optional(),
});
export type ReceivePurchaseOrderInput = z.infer<typeof receivePurchaseOrderSchema>;

/**
 * Absender (bestellende Gesellschaft) wechseln über Storno + Neuanlage — der Weg,
 * wenn ein Umnummerieren per PATCH nicht mehr zulässig ist: Beleg nicht mehr im
 * Entwurf, oder die alte Nummer ist im Kreis der bisherigen Gesellschaft nicht
 * mehr die zuletzt vergebene. `null` = keine Gesellschaft.
 */
export const changePurchaseLegalEntitySchema = z.object({ legalEntityId: z.string().uuid().nullable() });
export type ChangePurchaseLegalEntityInput = z.infer<typeof changePurchaseLegalEntitySchema>;

export const purchaseOrderResponseSchema = z.object({
  id: z.string().uuid(),
  tenantId: z.string().uuid(),
  kind: purchaseDocumentKindSchema,
  number: z.string(),
  status: purchaseOrderStatusSchema,
  /** Vorgängerbeleg (bei Umwandlung gesetzt), sonst null. */
  sourceDocumentId: z.string().uuid().nullable(),
  supplierId: z.string().uuid(),
  supplierName: z.string(),
  supplierAddress: z.string().nullable(),
  /** Ansprechpartner beim Lieferanten (Soft-Ref auf CRM-Contact) + Namens-Snapshot für „z. Hd.". */
  contactId: z.string().uuid().nullable(),
  contactName: z.string().nullable(),
  /**
   * Absender = bestellende Gesellschaft. Bestimmt den Nummernkreis und damit die
   * Belegnummer (ADR 0059), deshalb im Editor sichtbar und wechselbar.
   */
  legalEntityId: z.string().uuid().nullable(),
  legalEntityName: z.string().nullable(),
  projectId: z.string().uuid().nullable(),
  projectCode: z.string().nullable(),
  language: z.enum(['DE', 'EN']),
  currency: z.enum(['EUR', 'USD', 'CHF']),
  taxRate: z.number(),
  net: moneySchema,
  tax: moneySchema,
  gross: moneySchema,
  orderDate: z.string().datetime(),
  expectedDate: z.string().datetime().nullable(),
  dueDate: z.string().datetime().nullable(),
  receivedAt: z.string().datetime().nullable(),
  notes: z.string().nullable(),
  incoterm: incotermSchema.nullable(),
  incotermLocation: z.string().nullable(),
  internalReference: z.string().nullable(),
  assigneeUserId: z.string().uuid().nullable(),
  /** Kopftext (Markdown), vor der Positionstabelle gedruckt. */
  headerText: z.string().nullable(),
  /** Fußtext (Markdown), nach dem Summenblock gedruckt. */
  footerText: z.string().nullable(),
  /** Feld-Keys, die NICHT aufs PDF gedruckt werden (siehe PURCHASE_PDF_FIELDS). */
  pdfHiddenFields: z.array(z.string()),
  items: z.array(purchaseItemResponseSchema),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type PurchaseOrderResponse = z.infer<typeof purchaseOrderResponseSchema>;
